<script setup>
import { ref, computed, onMounted, onActivated, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useNexceoLeadLiveSync } from 'dashboard/composables/nexceo/useNexceoLeadLiveSync';
import leadsAPI from 'dashboard/api/nexceo/leads';
import nexceoProjectOptionsAPI from 'dashboard/api/nexceo/projectOptions';
import nexceoKanbanStagesAPI from 'dashboard/api/nexceo/kanbanStages';
import LeadDetailsDrawer from './kanban/LeadDetailsDrawer.vue';
import SiteVisitForm from './calendar/SiteVisitForm.vue';
import siteVisitsAPI from 'dashboard/api/nexceo/siteVisits';

const store = useStore();
const { accountId } = useAccount();
const router = useRouter();

const leads = ref([]);
const projects = ref([]);
const stages = ref([]);
const agents = computed(() => store.getters['agents/getAgents']);
const counts = ref({
  total: 0,
  unverified: 0,
  verified: 0,
  junk: 0,
  hot: 0,
  today: 0,
  followup: 0,
});
const pagination = ref({ page: 1, per_page: 50, total: 0, total_pages: 1 });

const isLoading = ref(false);
const loadError = ref('');
const selectedLead = ref(null);
const isDrawerOpen = ref(false);
const openMenuLeadId = ref(null);
const actioningId = ref(null);
const isVisitFormOpen = ref(null);
const visitFormPrefillType = ref('site_visit');

// Filters
const activeTab = ref('all');
const search = ref('');
const filterProject = ref('');
const filterChannel = ref('');
const filterVerification = ref('');
const filterStage = ref('');
const filterAgent = ref('');
const filterPriority = ref('');
const filterStartDate = ref('');
const filterEndDate = ref('');
const showFilters = ref(false);
const currentPage = ref(1);

const tabs = [
  { key: 'all', label: 'All', countKey: 'total' },
  { key: 'unverified', label: 'Unverified', countKey: 'unverified' },
  { key: 'verified', label: 'Verified', countKey: 'verified' },
  { key: 'hot', label: 'Hot', countKey: 'hot' },
  { key: 'junk', label: 'Junk / Rejected', countKey: 'junk' },
  { key: 'today', label: 'Today', countKey: 'today' },
  { key: 'followup', label: 'Follow-up Due', countKey: 'followup' },
];

const channelOptions = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'web_widget', label: 'Website' },
  { value: 'website_contact_form', label: 'Contact Form' },
  { value: 'website_popup', label: 'Popup Form' },
  { value: 'api', label: 'API / Manual' },
];
const priorityOptions = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];
const verificationOptions = [
  { value: 'unverified', label: 'Unverified' },
  { value: 'ai_verified', label: 'AI Verified' },
  { value: 'human_verified', label: 'Human Verified' },
  { value: 'rejected', label: 'Rejected' },
];

const activeFilterCount = computed(
  () =>
    [
      filterChannel,
      filterVerification,
      filterStage,
      filterAgent,
      filterPriority,
      filterStartDate,
      filterEndDate,
    ].filter(f => f.value).length
);

// ── Helpers ───────────────────────────────────────────────────────────────────

// Strip angle-bracket markup from names for safe display without mutating backend data.
function sanitizeLeadName(raw) {
  if (!raw) return 'Unknown Lead';
  return raw.replace(/<[^>]*>/g, '').trim() || 'Unknown Lead';
}

function formatBudget(lead) {
  const sym = lead.currency === 'INR' ? '₹' : lead.currency || '';
  const fmt = v => {
    const n = parseFloat(v);
    if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000) return `${Math.round(n / 100000)}L`;
    return n.toLocaleString('en-IN');
  };
  if (lead.budget_min && lead.budget_max)
    return `${sym}${fmt(lead.budget_min)}–${sym}${fmt(lead.budget_max)}`;
  if (lead.budget_max || lead.budget_min)
    return `${sym}${fmt(lead.budget_max || lead.budget_min)}`;
  return null;
}

function requirementLine(lead) {
  const p = [];
  if (lead.bedrooms) p.push(lead.bedrooms);
  if (lead.preferred_location) p.push(lead.preferred_location);
  const b = formatBudget(lead);
  if (b) p.push(b);
  return p.join(' · ') || null;
}

function displaySummary(lead) {
  if (lead.ai_summary) return lead.ai_summary;
  if (lead.preference_summary) return lead.preference_summary;
  const meta = lead.metadata || {};
  if (meta.summary) return meta.summary;
  if (meta.requirement_notes) return meta.requirement_notes;
  if (lead.requirement_notes) return lead.requirement_notes;
  const parts = [];
  if (lead.bedrooms) parts.push(lead.bedrooms);
  if (lead.preferred_location) parts.push(`in ${lead.preferred_location}`);
  const b = formatBudget(lead);
  if (b) parts.push(`Budget ${b}`);
  if (lead.purpose && lead.purpose !== 'unknown')
    parts.push(lead.purpose.replace(/_/g, ' '));
  if (lead.timeline && lead.timeline !== 'unknown')
    parts.push(lead.timeline.replace(/_/g, ' '));
  return parts.length ? parts.join(' · ') : null;
}

function formatDate(dt) {
  if (!dt) return null;
  return new Date(dt).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });
}

const CHANNEL_META = {
  whatsapp: {
    icon: 'i-lucide-message-circle',
    cls: 'text-green-600',
    label: 'WhatsApp',
  },
  facebook: {
    icon: 'i-lucide-facebook',
    cls: 'text-blue-600',
    label: 'Facebook',
  },
  instagram: {
    icon: 'i-lucide-instagram',
    cls: 'text-pink-500',
    label: 'Instagram',
  },
  email: { icon: 'i-lucide-mail', cls: 'text-n-slate-6', label: 'Email' },
  web_widget: {
    icon: 'i-lucide-globe',
    cls: 'text-woot-500',
    label: 'Website',
  },
  api: { icon: 'i-lucide-code-2', cls: 'text-n-slate-5', label: 'Manual' },
  manual: {
    icon: 'i-lucide-user-plus',
    cls: 'text-n-slate-5',
    label: 'Manual',
  },
  website_contact_form: {
    icon: 'i-lucide-file-text',
    cls: 'text-indigo-600',
    label: 'Contact Form',
  },
  website_popup: {
    icon: 'i-lucide-layout-panel-top',
    cls: 'text-violet-600',
    label: 'Popup Form',
  },
};
function channelMeta(ch) {
  return (
    CHANNEL_META[ch] || {
      icon: 'i-lucide-circle-dot',
      cls: 'text-n-slate-5',
      label: ch || 'Unknown',
    }
  );
}

function verificationBadge(lead) {
  if (lead.lead_status === 'junk')
    return { cls: 'bg-amber-100 text-amber-700', label: 'Junk' };
  const m = {
    human_verified: {
      cls: 'bg-green-100 text-green-700',
      label: 'Human Verified',
    },
    ai_verified: { cls: 'bg-blue-100 text-blue-700', label: 'AI Verified' },
    rejected: { cls: 'bg-red-100 text-red-600', label: 'Rejected' },
    unverified: { cls: 'bg-n-alpha-3 text-n-slate-9', label: 'Unverified' },
  };
  return m[lead.verification_status] || m.unverified;
}

function priorityBadge(p) {
  const m = {
    urgent: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    normal: 'bg-n-alpha-3 text-n-slate-9',
    low: 'bg-n-alpha-3 text-n-slate-8',
  };
  return m[p] || 'bg-n-alpha-3 text-n-slate-9';
}

const otherStagesFor = lead =>
  stages.value.filter(s => s.id !== lead.kanban_stage_id);

function isJunk(lead) {
  return lead.lead_status === 'junk';
}
function isRejectedOrJunk(lead) {
  return lead.rejected_or_junk === true;
}
function isVerified(lead) {
  return ['ai_verified', 'human_verified'].includes(lead.verification_status);
}

// ── Events ────────────────────────────────────────────────────────────────────

function toggleMenu(leadId) {
  openMenuLeadId.value = openMenuLeadId.value === leadId ? null : leadId;
}
function closeMenus() {
  openMenuLeadId.value = null;
}

async function loadData() {
  try {
    const [projRes, stagesRes] = await Promise.all([
      nexceoProjectOptionsAPI.get(),
      nexceoKanbanStagesAPI.getAll(),
    ]);
    projects.value = projRes.data?.projects ?? [];
    stages.value = Array.isArray(stagesRes.data)
      ? stagesRes.data
      : (stagesRes.data?.stages ?? []);
  } catch {
    /* non-critical */
  }
}

async function loadLeads() {
  isLoading.value = true;
  loadError.value = '';
  try {
    const params = { page: currentPage.value, per_page: 50 };
    if (activeTab.value !== 'all') params.tab = activeTab.value;
    if (filterProject.value) params.project_id = filterProject.value;
    if (filterChannel.value) params.source_channel = filterChannel.value;
    if (filterVerification.value)
      params.verification_status = filterVerification.value;
    if (filterStage.value) params.kanban_stage_id = filterStage.value;
    if (filterAgent.value) params.agent_id = filterAgent.value;
    if (filterPriority.value) params.priority = filterPriority.value;
    if (filterStartDate.value) params.date_from = filterStartDate.value;
    if (filterEndDate.value) params.date_to = filterEndDate.value;
    if (search.value.trim()) params.search = search.value.trim();

    const { data } = await leadsAPI.getAllLeads(params);
    leads.value = data.leads ?? [];
    pagination.value = data.pagination ?? pagination.value;
    counts.value = { ...counts.value, ...(data.counts ?? {}) };
  } catch {
    loadError.value = 'Failed to load leads.';
  } finally {
    isLoading.value = false;
  }
}

function switchTab(key) {
  activeTab.value = key;
  currentPage.value = 1;
  filterVerification.value = '';
  loadLeads();
}

function clearFilters() {
  filterChannel.value = '';
  filterVerification.value = '';
  filterStage.value = '';
  filterAgent.value = '';
  filterPriority.value = '';
  filterStartDate.value = '';
  filterEndDate.value = '';
  currentPage.value = 1;
  loadLeads();
}

watch(
  [
    filterProject,
    filterChannel,
    filterVerification,
    filterStage,
    filterAgent,
    filterPriority,
    filterStartDate,
    filterEndDate,
  ],
  () => {
    currentPage.value = 1;
    loadLeads();
  }
);
let searchTimer = null;
watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    loadLeads();
  }, 350);
});

// M26.15.7 — shared with VerifiedLeadsPage.vue (see the composable's own
// comment for why this page keeps its own paginated `leads` ref instead of
// the nexceoLeads Vuex store).
useNexceoLeadLiveSync(leads, selectedLead);

onMounted(async () => {
  await Promise.all([loadData(), loadLeads(), store.dispatch('agents/get')]);
});
onActivated(() => {
  loadLeads();
});

function openDrawer(lead) {
  selectedLead.value = lead;
  isDrawerOpen.value = true;
  closeMenus();
}
async function onDrawerUpdated() {
  await loadLeads();
  if (selectedLead.value) {
    const refreshed = leads.value.find(l => l.id === selectedLead.value.id);
    if (refreshed) selectedLead.value = refreshed;
  }
}
function openEditLead(lead) {
  closeMenus();
  router.push({
    name: 'nexceo_leads_edit',
    params: { accountId: accountId.value, id: lead.id },
    query: { return_to: 'all_leads' },
  });
}
function openLeadForm() {
  router.push({
    name: 'nexceo_leads_new',
    params: { accountId: accountId.value },
    query: { return_to: 'all_leads', verified: 'false' },
  });
}

async function onVerify(lead) {
  closeMenus();
  actioningId.value = lead.id;
  try {
    await leadsAPI.verify(lead.id);
    useAlert('Lead verified and moved to Qualified.');
    await loadLeads();
  } catch {
    useAlert('Failed to verify lead.');
  } finally {
    actioningId.value = null;
  }
}
async function onMarkJunk(lead) {
  closeMenus();
  // eslint-disable-next-line no-alert
  const reason = window.prompt('Junk reason (optional):');
  if (reason === null) return;
  actioningId.value = lead.id;
  try {
    await leadsAPI.markJunk(lead.id, reason);
    useAlert('Lead marked as junk.');
    await loadLeads();
  } catch {
    useAlert('Failed to mark junk.');
  } finally {
    actioningId.value = null;
  }
}
async function onRestore(lead) {
  closeMenus();
  actioningId.value = lead.id;
  try {
    await leadsAPI.restoreLead(lead.id);
    useAlert('Lead restored.');
    await loadLeads();
  } catch {
    useAlert('Failed to restore lead.');
  } finally {
    actioningId.value = null;
  }
}
async function onMarkLost(lead) {
  closeMenus();
  actioningId.value = lead.id;
  try {
    await leadsAPI.markLost(lead.id, 'Marked lost from All Leads');
    useAlert('Lead marked as lost.');
    await loadLeads();
  } catch {
    useAlert('Failed to mark lead as lost.');
  } finally {
    actioningId.value = null;
  }
}
async function onSendTeamAlert(lead, force = false) {
  closeMenus();
  actioningId.value = lead.id;
  try {
    await leadsAPI.sendTeamAlert(lead.id, { force });
    useAlert('Team alert sent.');
  } catch (err) {
    const msg =
      err?.response?.data?.message || err?.response?.data?.error || '';
    if (!force && msg.includes('already sent')) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Alert already sent. Resend?')) {
        await onSendTeamAlert(lead, true);
      }
      return;
    }
    useAlert(msg || 'Failed to send team alert.');
  } finally {
    actioningId.value = null;
  }
}
function openScheduleVisit(lead, type = 'site_visit') {
  closeMenus();
  visitFormPrefillType.value = type;
  isVisitFormOpen.value = lead;
}
async function resendVisitConfirmation(lead) {
  closeMenus();
  if (!lead.next_site_visit_id) return;
  try {
    await siteVisitsAPI.resendConfirmation(lead.next_site_visit_id);
    useAlert('Confirmation resent.');
  } catch {
    useAlert('Failed to resend confirmation.');
  }
}
async function onMoveStage(lead, stageKey) {
  closeMenus();
  try {
    await leadsAPI.moveStage(lead.id, stageKey);
    await loadLeads();
  } catch {
    useAlert('Failed to move stage.');
  }
}
function onVisitSaved() {
  isVisitFormOpen.value = null;
  loadLeads();
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- ── Header ─────────────────────────────────────────────────────────── -->
    <div
      class="flex items-center gap-3 px-4 h-14 border-b border-n-weak shrink-0"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span class="i-lucide-list-filter size-4 text-woot-500 shrink-0" />
        <h1 class="text-sm font-semibold text-n-slate-12 whitespace-nowrap">
          All Leads
        </h1>
        <span
          class="text-xs text-n-slate-7 bg-n-alpha-2 px-1.5 py-0.5 rounded-full tabular-nums shrink-0"
        >
          {{ pagination.total }}
        </span>
      </div>

      <div class="flex items-center gap-2 ml-auto shrink-0">
        <!-- Search -->
        <div class="relative hidden sm:block">
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3 text-n-slate-6 pointer-events-none"
          />
          <input
            v-model="search"
            type="text"
            placeholder="Search name, phone, email…"
            class="pl-7 pr-3 py-1.5 text-xs border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none focus:border-woot-500 w-40 lg:w-52 transition-all"
          />
        </div>

        <!-- Filter toggle -->
        <button
          class="relative flex items-center justify-center w-7 h-7 rounded-lg border transition-colors"
          :class="
            showFilters
              ? 'bg-woot-500 border-woot-500 text-white'
              : 'border-n-weak text-n-slate-7 hover:border-woot-400 hover:text-woot-500'
          "
          title="Advanced filters"
          @click="showFilters = !showFilters"
        >
          <span class="i-lucide-sliders-horizontal size-3.5" />
          <span
            v-if="activeFilterCount"
            class="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-ruby-500 text-white text-[9px] font-bold flex items-center justify-center leading-none"
            >{{ activeFilterCount }}</span
          >
        </button>

        <!-- Add Lead -->
        <button
          class="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors"
          @click.prevent.stop="openLeadForm"
        >
          <span class="i-lucide-plus size-3.5" />
          <span class="hidden sm:inline">Add Lead</span>
        </button>
      </div>
    </div>

    <!-- ── Quick tabs + project filter row ────────────────────────────────── -->
    <div
      class="flex items-center gap-0 px-2 border-b border-n-weak shrink-0 bg-n-background overflow-x-auto scrollbar-hide"
    >
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="flex items-center gap-1 px-2.5 py-2 text-xs whitespace-nowrap border-b-2 transition-colors shrink-0"
        :class="
          activeTab === tab.key
            ? 'border-woot-500 text-woot-600 font-semibold'
            : 'border-transparent text-n-slate-9 hover:text-n-slate-12'
        "
        @click="switchTab(tab.key)"
      >
        {{ tab.label }}
        <span
          v-if="counts[tab.countKey]"
          class="text-[9px] px-1 py-0.5 rounded-full font-bold tabular-nums min-w-[16px] text-center"
          :class="
            activeTab === tab.key
              ? 'bg-woot-100 text-woot-700'
              : 'bg-n-alpha-3 text-n-slate-9'
          "
          >{{ counts[tab.countKey] }}</span
        >
      </button>

      <!-- Project filter — always visible, compact, pushed right -->
      <div class="flex items-center gap-1.5 ml-auto pl-3 shrink-0">
        <select
          v-model="filterProject"
          class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 max-w-[160px]"
        >
          <option value="">All Projects</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- ── Advanced filters panel (collapsed by default) ─────────────────── -->
    <transition name="slide-down">
      <div
        v-if="showFilters"
        class="shrink-0 border-b border-n-weak bg-n-alpha-1 dark:bg-n-solid-2"
      >
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 px-4 py-3">
          <select v-model="filterChannel" class="filter-select">
            <option value="">All Channels</option>
            <option v-for="c in channelOptions" :key="c.value" :value="c.value">
              {{ c.label }}
            </option>
          </select>
          <select v-model="filterVerification" class="filter-select">
            <option value="">All Statuses</option>
            <option
              v-for="v in verificationOptions"
              :key="v.value"
              :value="v.value"
            >
              {{ v.label }}
            </option>
          </select>
          <select v-model="filterStage" class="filter-select">
            <option value="">All Stages</option>
            <option v-for="s in stages" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
          <select v-model="filterAgent" class="filter-select">
            <option value="">All Agents</option>
            <option v-for="a in agents" :key="a.id" :value="a.id">
              {{ a.name }}
            </option>
          </select>
          <select v-model="filterPriority" class="filter-select">
            <option value="">All Priorities</option>
            <option
              v-for="p in priorityOptions"
              :key="p.value"
              :value="p.value"
            >
              {{ p.label }}
            </option>
          </select>
          <input
            v-model="filterStartDate"
            type="date"
            class="filter-select"
            title="From date"
          />
          <input
            v-model="filterEndDate"
            type="date"
            class="filter-select"
            title="To date"
          />
          <button
            v-if="activeFilterCount"
            class="text-xs text-ruby-600 hover:text-ruby-700 font-medium px-3 py-1.5 rounded-lg hover:bg-ruby-50 border border-ruby-200 transition-colors"
            @click="clearFilters"
          >
            Clear filters
          </button>
        </div>
      </div>
    </transition>

    <!-- ── Error ──────────────────────────────────────────────────────────── -->
    <div
      v-if="loadError"
      class="flex items-center gap-2 px-4 py-2 bg-ruby-50 border-b border-ruby-100 text-xs text-ruby-700 shrink-0"
    >
      <span class="i-lucide-alert-circle size-3.5 shrink-0" />
      {{ loadError }}
      <button class="underline font-medium ml-1" @click="loadLeads">
        Retry
      </button>
    </div>

    <!-- ── Table ─────────────────────────────────────────────────────────── -->
    <div class="flex-1 overflow-auto">
      <table class="w-full border-collapse text-xs min-w-[1100px]">
        <!-- Sticky header -->
        <thead class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
          <tr>
            <th
              class="th sticky left-0 z-20 bg-n-background min-w-[200px] max-w-[220px]"
            >
              Lead
            </th>
            <th class="th w-28">Mobile</th>
            <th class="th w-28">Project</th>
            <th class="th w-36">Requirement</th>
            <th class="th max-w-[200px]">Summary</th>
            <th class="th w-20">Priority</th>
            <th class="th w-28">Stage</th>
            <th class="th w-28">Status</th>
            <th class="th w-24 hidden md:table-cell">Agent</th>
            <th class="th w-20 hidden lg:table-cell">Created</th>
            <th class="th w-20 hidden lg:table-cell">Follow-up</th>
            <th class="th w-24">Visit</th>
            <th class="th w-20 text-right">Actions</th>
          </tr>
        </thead>

        <!-- Skeleton rows while loading -->
        <tbody v-if="isLoading && leads.length === 0">
          <tr v-for="i in 8" :key="i" class="border-b border-n-weak">
            <td class="px-4 py-2.5 sticky left-0 bg-n-background">
              <div class="h-3.5 w-28 rounded bg-n-alpha-3 animate-pulse mb-1" />
              <div class="h-2.5 w-20 rounded bg-n-alpha-2 animate-pulse" />
            </td>
            <td v-for="j in 11" :key="j" class="px-3 py-2.5">
              <div
                class="h-3 rounded bg-n-alpha-2 animate-pulse"
                :style="`width:${40 + j * 5}%`"
              />
            </td>
          </tr>
        </tbody>

        <!-- Data rows -->
        <tbody v-else-if="leads.length">
          <tr
            v-for="lead in leads"
            :key="lead.id"
            class="border-b border-n-weak hover:bg-n-alpha-1 dark:hover:bg-n-solid-2 transition-colors cursor-pointer group"
            :class="isRejectedOrJunk(lead) ? 'opacity-60' : ''"
            @click="openDrawer(lead)"
          >
            <!-- Lead name + channel -->
            <td
              class="px-4 py-2 sticky left-0 z-10 bg-n-background group-hover:bg-n-alpha-1 dark:group-hover:bg-n-solid-2 transition-colors min-w-[200px] max-w-[220px]"
            >
              <div
                class="font-semibold text-n-slate-12 truncate leading-tight"
                :title="sanitizeLeadName(lead.name)"
              >
                {{ sanitizeLeadName(lead.name) }}
              </div>
              <div
                v-if="lead.email"
                class="text-[10px] text-n-slate-11 truncate mt-0.5"
              >
                {{ lead.email }}
              </div>
              <div class="flex items-center gap-1 mt-0.5">
                <span
                  class="size-2.5 shrink-0"
                  :class="[
                    channelMeta(lead.source_channel).icon,
                    channelMeta(lead.source_channel).cls,
                  ]"
                />
                <span class="text-[10px] text-n-slate-11">{{
                  channelMeta(lead.source_channel).label
                }}</span>
              </div>
            </td>

            <!-- Mobile -->
            <td
              class="px-3 py-2 text-n-slate-12 tabular-nums whitespace-nowrap"
            >
              {{ lead.phone_number || '—' }}
            </td>

            <!-- Project -->
            <td class="px-3 py-2">
              <span v-if="lead.project" class="text-n-slate-12">{{
                lead.project.name
              }}</span>
              <span v-else class="text-n-slate-7">Unassigned</span>
            </td>

            <!-- Requirement -->
            <td class="px-3 py-2">
              <span v-if="requirementLine(lead)" class="text-n-slate-12">{{
                requirementLine(lead)
              }}</span>
              <span v-else class="text-n-slate-7">Not captured</span>
            </td>

            <!-- Summary -->
            <td class="px-3 py-2 max-w-[200px]">
              <p
                v-if="displaySummary(lead)"
                class="text-n-slate-12 line-clamp-2 leading-snug"
                :title="displaySummary(lead)"
              >
                {{ displaySummary(lead) }}
              </p>
              <span v-else class="text-n-slate-7">No summary yet</span>
            </td>

            <!-- Priority -->
            <td class="px-3 py-2">
              <span
                class="badge capitalize"
                :class="priorityBadge(lead.priority)"
              >
                {{ lead.priority }}
              </span>
            </td>

            <!-- Stage -->
            <td class="px-3 py-2">
              <span v-if="lead.kanban_stage" class="text-n-slate-12">{{
                lead.kanban_stage.name
              }}</span>
              <span v-else class="text-n-slate-7">—</span>
            </td>

            <!-- Verification / junk status -->
            <td class="px-3 py-2">
              <span class="badge" :class="verificationBadge(lead).cls">{{
                verificationBadge(lead).label
              }}</span>
            </td>

            <!-- Agent (hidden on small screens) -->
            <td class="px-3 py-2 hidden md:table-cell">
              <span v-if="lead.assigned_agent" class="text-n-slate-12">{{
                lead.assigned_agent.name
              }}</span>
              <span v-else class="text-n-slate-7">Unassigned</span>
            </td>

            <!-- Created (hidden on medium screens) -->
            <td
              class="px-3 py-2 text-n-slate-12 whitespace-nowrap hidden lg:table-cell"
            >
              {{ formatDate(lead.created_at) || '—' }}
            </td>

            <!-- Follow-up (hidden on medium screens) -->
            <td
              class="px-3 py-2 text-n-slate-12 whitespace-nowrap hidden lg:table-cell"
            >
              {{ formatDate(lead.next_follow_up_at) || '—' }}
            </td>

            <!-- Visit -->
            <td class="px-3 py-2">
              <template
                v-if="
                  lead.site_visit_status &&
                  lead.site_visit_status !== 'not_scheduled'
                "
              >
                <span class="text-green-600 font-medium capitalize">
                  {{
                    lead.next_site_visit_type === 'video_call'
                      ? 'Video Call'
                      : lead.site_visit_status.replace(/_/g, ' ')
                  }}
                </span>
                <span
                  v-if="lead.next_site_visit_at"
                  class="block text-[10px] text-n-slate-11 mt-0.5"
                >
                  {{ formatDate(lead.next_site_visit_at) }}
                </span>
                <a
                  v-if="lead.next_site_visit_meeting_url"
                  :href="lead.next_site_visit_meeting_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-0.5 text-[10px] text-woot-500 hover:underline mt-0.5"
                  @click.stop
                >
                  <span class="i-lucide-video size-2.5" /> Join
                </a>
              </template>
              <span v-else class="text-n-slate-7">Not scheduled</span>
            </td>

            <!-- Actions -->
            <td class="px-3 py-2" @click.stop>
              <div class="relative flex items-center justify-end">
                <button
                  type="button"
                  class="flex items-center gap-0.5 px-2 py-1.5 text-[10px] font-medium text-n-slate-9 border border-n-weak rounded-lg hover:border-woot-400 hover:text-woot-600 transition-colors disabled:opacity-40"
                  :disabled="actioningId === lead.id"
                  @click.stop="toggleMenu(lead.id)"
                >
                  <span
                    v-if="actioningId === lead.id"
                    class="i-lucide-loader-2 size-3 animate-spin"
                  />
                  <template v-else>
                    <span class="i-lucide-more-horizontal size-3.5" />
                  </template>
                </button>

                <!-- Dropdown — inline z-20 so it sits above the z-10 click-outside overlay -->
                <div
                  v-if="openMenuLeadId === lead.id"
                  class="absolute right-0 top-full mt-1 z-20 w-52 bg-white dark:bg-n-solid-3 border border-n-weak rounded-xl shadow-lg py-1 text-sm"
                >
                  <!-- View / Edit -->
                  <button
                    type="button"
                    class="dropdown-item"
                    @click.stop="openDrawer(lead)"
                  >
                    <span class="i-lucide-eye size-4 text-n-slate-9" /> View
                    Details
                  </button>
                  <button
                    v-if="!isJunk(lead)"
                    type="button"
                    class="dropdown-item"
                    @click.stop="openEditLead(lead)"
                  >
                    <span class="i-lucide-pencil size-4 text-woot-500" /> Edit
                    Lead
                  </button>

                  <!-- Open Conversation -->
                  <a
                    v-if="lead.conversation_id"
                    :href="`/app/accounts/${accountId}/conversations/${lead.conversation_id}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="dropdown-item"
                    @click.stop="closeMenus()"
                  >
                    <span
                      class="i-lucide-external-link size-4 text-n-slate-9"
                    />
                    Open Conversation
                  </a>
                  <button
                    v-else
                    type="button"
                    class="dropdown-item opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <span
                      class="i-lucide-external-link size-4 text-n-slate-7"
                    />
                    No Conversation
                  </button>

                  <template v-if="!isJunk(lead)">
                    <div class="dropdown-separator" />

                    <button
                      v-if="!isVerified(lead)"
                      type="button"
                      class="dropdown-item text-green-700 hover:bg-green-50"
                      @click.stop="onVerify(lead)"
                    >
                      <span class="i-lucide-badge-check size-4" /> Mark as
                      Verified
                    </button>

                    <!-- Schedule -->
                    <div class="dropdown-section-label">Schedule</div>
                    <button
                      type="button"
                      class="dropdown-item"
                      @click.stop="openScheduleVisit(lead, 'site_visit')"
                    >
                      <span class="i-lucide-map-pin size-4 text-green-600" />
                      Site Visit
                    </button>
                    <button
                      type="button"
                      class="dropdown-item"
                      @click.stop="openScheduleVisit(lead, 'video_call')"
                    >
                      <span class="i-lucide-video size-4 text-woot-500" />
                      Google Meet
                    </button>
                    <button
                      type="button"
                      class="dropdown-item"
                      @click.stop="openScheduleVisit(lead, 'office_meeting')"
                    >
                      <span class="i-lucide-building-2 size-4 text-n-slate-9" />
                      Agent Follow Up
                    </button>
                    <button
                      v-if="lead.next_site_visit_id"
                      type="button"
                      class="dropdown-item"
                      @click.stop="resendVisitConfirmation(lead)"
                    >
                      <span class="i-lucide-send size-4 text-n-slate-9" />
                      Resend Confirmation
                    </button>

                    <!-- Move Stage -->
                    <div class="px-2 pt-1 pb-1">
                      <select
                        class="w-full text-xs text-n-slate-9 bg-n-alpha-1 border border-n-weak rounded-md px-2 py-1 outline-none hover:border-woot-400 cursor-pointer"
                        @change.stop="
                          e => {
                            if (e.target.value) {
                              onMoveStage(lead, e.target.value);
                              e.target.value = '';
                            }
                          }
                        "
                        @click.stop
                      >
                        <option value="" disabled selected>Move Stage…</option>
                        <option
                          v-for="s in otherStagesFor(lead)"
                          :key="s.id"
                          :value="s.key"
                        >
                          {{ s.name }}
                        </option>
                      </select>
                    </div>

                    <div class="dropdown-separator" />

                    <button
                      v-if="isVerified(lead)"
                      type="button"
                      class="dropdown-item text-woot-700 hover:bg-woot-50"
                      @click.stop="onSendTeamAlert(lead)"
                    >
                      <span class="i-lucide-bell-ring size-4" /> Send Team Alert
                    </button>
                    <button
                      type="button"
                      class="dropdown-item text-amber-700 hover:bg-amber-50"
                      @click.stop="onMarkLost(lead)"
                    >
                      <span class="i-lucide-thumbs-down size-4" /> Mark Lost
                    </button>
                    <button
                      type="button"
                      class="dropdown-item text-red-600 hover:bg-red-50"
                      @click.stop="onMarkJunk(lead)"
                    >
                      <span class="i-lucide-trash-2 size-4" /> Mark as Junk
                    </button>
                  </template>

                  <template v-else>
                    <div class="dropdown-separator" />
                    <button
                      type="button"
                      class="dropdown-item text-green-700 hover:bg-green-50"
                      @click.stop="onRestore(lead)"
                    >
                      <span class="i-lucide-rotate-ccw size-4" /> Restore Lead
                    </button>
                    <p
                      v-if="lead.junk_reason"
                      class="px-3 py-1 text-[10px] text-n-slate-8"
                    >
                      {{ lead.junk_reason }}
                    </p>
                  </template>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty state (inside scroll area so table header stays) -->
      <div
        v-if="!isLoading && leads.length === 0"
        class="flex flex-col items-center justify-center gap-4 py-16"
      >
        <div
          class="flex items-center justify-center w-12 h-12 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
        >
          <span class="i-lucide-list-filter size-6 text-woot-500" />
        </div>
        <div class="text-center max-w-xs">
          <p class="text-sm font-semibold text-n-slate-12">No leads found</p>
          <p class="text-xs text-n-slate-9 mt-1">
            {{
              activeFilterCount || search
                ? 'No leads match these filters.'
                : 'Leads from WhatsApp, Instagram, Facebook, Website, and manual entry will appear here.'
            }}
          </p>
          <button
            v-if="activeFilterCount"
            class="mt-3 text-xs text-woot-600 hover:underline font-medium"
            @click="clearFilters"
          >
            Clear filters
          </button>
        </div>
      </div>
    </div>

    <!-- ── Pagination ─────────────────────────────────────────────────────── -->
    <div
      v-if="pagination.total_pages > 1"
      class="flex items-center justify-between px-4 py-2.5 border-t border-n-weak shrink-0 bg-n-background"
    >
      <span class="text-xs text-n-slate-9 tabular-nums">
        Page {{ pagination.page }} of {{ pagination.total_pages }} ·
        {{ pagination.total }} leads
      </span>
      <div class="flex items-center gap-1">
        <button
          class="px-2.5 py-1 text-xs border border-n-weak rounded-lg text-n-slate-8 hover:border-woot-400 hover:text-woot-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="currentPage <= 1"
          @click="
            () => {
              currentPage--;
              loadLeads();
            }
          "
        >
          ← Prev
        </button>
        <button
          class="px-2.5 py-1 text-xs border border-n-weak rounded-lg text-n-slate-8 hover:border-woot-400 hover:text-woot-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="currentPage >= pagination.total_pages"
          @click="
            () => {
              currentPage++;
              loadLeads();
            }
          "
        >
          Next →
        </button>
      </div>
    </div>
  </div>

  <!-- ── Panels ─────────────────────────────────────────────────────────── -->
  <LeadDetailsDrawer
    :lead="selectedLead"
    :is-open="isDrawerOpen"
    :stages="stages"
    :agents="agents"
    @close="
      isDrawerOpen = false;
      selectedLead = null;
    "
    @updated="onDrawerUpdated"
  />

  <SiteVisitForm
    :is-open="!!isVisitFormOpen"
    :prefill-lead="isVisitFormOpen"
    :prefill-visit-type="visitFormPrefillType"
    @close="isVisitFormOpen = null"
    @saved="onVisitSaved"
  />

  <!-- Click-outside overlay -->
  <div v-if="openMenuLeadId" class="fixed inset-0 z-10" @click="closeMenus" />
</template>

<style scoped>
/* ── Slide transition ─────────────────────────────────────────────────────── */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.15s ease;
  overflow: hidden;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
}
.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 120px;
}

/* ── Table header cell (raw CSS — avoids @apply with arbitrary values) ─────── */
.th {
  text-align: left;
  padding: 0.5rem 0.75rem;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  @apply text-n-slate-9;
}

/* ── Compact filter selects ───────────────────────────────────────────────── */
.filter-select {
  @apply w-full text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500;
}

/* ── Badge base — color classes come from JS verificationBadge()/priorityBadge() ── */
.badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
  white-space: nowrap;
}

/* ── Dropdown panel ───────────────────────────────────────────────────────── */
.dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 0.25rem;
  z-index: 30;
  width: 12rem;
  border-radius: 0.75rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 0.75rem;
  @apply bg-n-background border border-n-weak shadow-lg;
}
.dropdown-item {
  @apply flex items-center gap-2 w-full px-3 py-1.5 text-n-slate-12 hover:bg-n-alpha-2 transition-colors;
}
.dropdown-separator {
  @apply border-t border-n-weak my-1;
}
.dropdown-section-label {
  padding: 0.25rem 0.75rem 0.125rem;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  @apply text-n-slate-6;
}

/* ── Scrollbar hide for tab row ───────────────────────────────────────────── */
.scrollbar-hide {
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* ── Two-line clamp ───────────────────────────────────────────────────────── */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
